import React, { useEffect, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { getCSSVariable } from '@opendoor/bricks-next';
import { IconLayoutDashboard } from '@tabler/icons-react';
import { navTransactionIcon } from 'components/account/navTransactionIcon';
import { getSessionExtras } from 'components/api';
import { isDashboardSlug } from 'components/landing-pages-v2/utils/slug';
import { useObservability } from 'helpers/observability';
import { useHasActiveBuyerOffers } from 'helpers/useBuyerOffers';
import { useLeads } from 'helpers/useLeads';
import { navTransactionLabel, useNavTransactions } from 'helpers/useNavTransactions';

import { MenuLinkItem } from '../components/MenuLinkItem';

interface MobileMenuAccountItemsProps {
  slug: string;
}

const ICON_SIZE = 20;
const ICON_STROKE = 1.5;

/**
 * Redesigned mobile account items (buyer-hub flag on): Dashboard, active
 * transactions, and Account settings. Rendered inside the account section of
 * {@link MobileMenuList}.
 */
export const MobileMenuAccountItemsV2: React.FC<MobileMenuAccountItemsProps> = ({ slug }) => {
  const { trackEvent } = useObservability();
  const transactions = useNavTransactions();
  const iconColor = getCSSVariable('$contentPrimary', 'color');

  return (
    <>
      <MenuLinkItem
        key="dashboard"
        href="/dashboard"
        name="Dashboard"
        analyticsName="cosmos-mobile-nav-dashboard"
        isActive={isDashboardSlug(slug)}
        icon={<IconLayoutDashboard size={ICON_SIZE} stroke={ICON_STROKE} color={iconColor} />}
        onPress={() =>
          trackEvent('cta-click', 'dashboard-link', undefined, {
            categoryOverride: 'homepage',
          })
        }
      />
      {transactions.map((transaction) => {
        const Icon = navTransactionIcon(transaction.type);
        return (
          <MenuLinkItem
            key={transaction.id}
            href={transaction.href}
            name={navTransactionLabel(transaction)}
            analyticsName={`cosmos-mobile-nav-${transaction.type}`}
            isActive={false}
            icon={<Icon size={ICON_SIZE} stroke={ICON_STROKE} color={iconColor} />}
          />
        );
      })}
      <MenuLinkItem
        key="account"
        href="/account"
        name="Account settings"
        analyticsName=""
        isActive={slug === '/account'}
        isLast
        onPress={() =>
          trackEvent('cta-click', 'account-link', undefined, { categoryOverride: 'homepage' })
        }
      />
    </>
  );
};

/**
 * Previous mobile account items (buyer-hub flag off): leads, resale dashboard,
 * active-offer dashboard, and Account — unchanged for users outside the rollout.
 */
export const MobileMenuAccountItemsLegacy: React.FC<MobileMenuAccountItemsProps> = ({ slug }) => {
  const { user: human } = useAuth();
  const { trackEvent } = useObservability();
  const leads = useLeads();
  const { hasActiveOffers } = useHasActiveBuyerOffers(true);
  const [hasResaleDashboard, setHasResaleDashboard] = useState(false);
  const hasLeads = leads && leads.length > 0;

  useEffect(() => {
    if (!human) {
      return;
    }
    getSessionExtras().then((extras) => {
      setHasResaleDashboard(extras.has_resale_closing_dashboard);
    });
  }, [human?.humanToken, human?.customerUuid]);

  return (
    <>
      {hasLeads &&
        leads
          .slice(0, 3)
          .map((lead) => (
            <MenuLinkItem
              key={lead.id}
              href={lead.url}
              name={`${lead.address.street || ''}${
                lead.readable_offer_state ? ` (${lead.readable_offer_state})` : ''
              }`}
              analyticsName=""
              isActive={false}
            />
          ))}
      {leads && leads.length > 3 && (
        <MenuLinkItem
          key="more-leads"
          href="/properties"
          name="More"
          analyticsName=""
          isActive={slug === '/properties'}
        />
      )}
      {hasResaleDashboard && (
        <MenuLinkItem
          key="closing-dash"
          href="/closing-dashboard"
          name="Closing dash"
          analyticsName=""
          isActive={slug === '/closing-dashboard'}
        />
      )}
      {hasActiveOffers && (
        <MenuLinkItem
          key="dashboard"
          href="/dashboard"
          name="Dashboard"
          analyticsName=""
          isActive={slug === '/dashboard'}
          onPress={() =>
            trackEvent('cta-click', 'dashboard-link', undefined, {
              categoryOverride: 'homepage',
            })
          }
        />
      )}
      <MenuLinkItem
        key="account"
        href="/account"
        name="Account"
        analyticsName=""
        isActive={slug === '/account'}
        isLast
        onPress={() =>
          trackEvent('cta-click', 'account-link', undefined, { categoryOverride: 'homepage' })
        }
      />
    </>
  );
};
